import { DateTimePicker, Input, Toggle, Typography } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { CardContent, Section } from '@hero/atoms';
import { AvailableCharitiesModal, DonateToCharityModal } from '@hero/components/modals';
import { OverviewAdvanceSettings } from '@hero/components/organisms';
import { configs } from '@hero/configs';
import {
  MAX_COST_CENTER_LENGTH,
  MAX_FEATURE_NAME_LENGTH,
  MAX_PO_LENGTH,
  NON_EDITABLE_RECOGNITION_STATUS,
  PAYMENT_METHOD_TYPES,
  SOURCE_TYPES
} from '@hero/constants';
import { DetailItem, OnboardingCard, PaymentMethodInput } from '@hero/molecules';
import {
  useGetRecognition,
  useUpdateRecognition
} from '@hero/services/order-service/recognition/hooks';
import { useGetUser } from '@hero/services/organization-service/hooks';
import { useOnboardingStore } from '@hero/store/hooks';

import { withMerchantComponents } from '../../../../../utils/merchantUtils';

import * as S from './style';

function Overview({ onClickOnboardingInfoCard, isApprovalProcessing }) {
  const { t } = useTranslation();
  const { recognitionCode } = useParams();
  const { data: recognition } = useGetRecognition({ recognitionCode });
  const { mutateAsync: updateRecognition, isLoading: isLoadingUpdateRecognition } =
    useUpdateRecognition();
  const { recognition: onboardingRecognition } = useOnboardingStore();
  const { data: user } = useGetUser();

  const [poNumber, setPoNumber] = useState();
  const [costCenter, setCostCenter] = useState();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState();
  const [recognitionName, setRecognitionName] = useState();
  const [expirationDate, setExpirationDate] = useState();
  const [disableReGifting, setDisableReGifting] = useState();
  const [allowPremiumDates, setAllowPremiumDates] = useState();
  const [isOnboardingCardSettings, setIsOnboardingCardSettings] = useState(false);
  const [advanceSettings, setAdvanceSettings] = useState({
    settings: {
      onlyCollectAddress: false,
      disableScratchEffect: false,
      receiveThankYouNotes: false
    }
  });
  const [showCharityInfoModal, setShowCharityInfoModal] = useState(false);
  const [showSelectCharitiesModal, setShowSelectCharitiesModal] = useState(false);

  const messages = {
    requireMeeting: t('requireMeetingDesc'),
    itemReGift: t('itemReGiftDesc'),
    premiumDates: t('premiumDatesDesc')
  };

  const isDetailItemEditable = React.useMemo(
    () => !NON_EDITABLE_RECOGNITION_STATUS.includes(recognition?.status?.current),
    [recognition]
  );

  const advanceSettingsEnabled = useMemo(() => {
    return (
      (configs?.advanceSettings?.enabled && recognition?.source !== SOURCE_TYPES.teams_app) ||
      configs?.advanceSettings?.reGiftingEnabled ||
      configs?.advanceSettings?.charityEnabled ||
      configs?.advanceSettings?.premiumDates ||
      configs?.advanceSettings?.onlyCollectAddressEnabled ||
      configs?.advanceSettings?.scratchEffectEnabled
    );
  }, [configs?.advanceSettings]);

  useEffect(() => {
    if (recognition?.name) {
      setRecognitionName(recognition.name);
      setPoNumber(recognition.meta?.poNumber);
      setCostCenter(recognition.meta?.costCenter);
      setSelectedPaymentMethod(recognition.paymentMethod);
      setExpirationDate(recognition.settings?.expiresAt);
      setDisableReGifting(recognition.settings?.disableReGifting);
      setAllowPremiumDates(recognition.settings?.allowPremiumDates);
      setAdvanceSettings({
        settings: {
          onlyCollectAddress: recognition.settings?.onlyCollectAddress,
          charityRules: recognition.settings?.charityRules,
          disableScratchEffect: recognition.settings?.disableScratchEffect,
          receiveThankYouNotes: recognition.settings?.receiveThankYouNotes
        }
      });

      if (!configs.advanceSettings.enabled && !recognition.settings?.disableReGifting) {
        updateRecognition({
          params: { recognitionCode },
          body: {
            settings: {
              disableReGifting: true
            }
          }
        });
      }
    }
  }, [recognition]);

  const isTeams = recognition?.source === SOURCE_TYPES.teams_app;

  const isCCPayment = recognition?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard;

  function handlePaymentMethod(spm) {
    setSelectedPaymentMethod(spm);

    updateRecognition({
      params: { recognitionCode },
      body: {
        paymentMethod: spm
      }
    });
  }

  const getDatePickerValue = () => {
    if (expirationDate) {
      return new Date(expirationDate);
    }
    if (isApprovalProcessing) {
      return undefined;
    }
    return null;
  };

  return (
    <S.OverviewWrapper>
      <CardContent>
        {configs.paymentMethods.enabled && (
          <DetailItem title={`${t('paymentMethod')}:`} titleStyle={{ marginTop: '10px' }}>
            <PaymentMethodInput
              key={recognition}
              onTourButtonClick={() => setIsOnboardingCardSettings(true)}
              featureType="recognition"
              selectedPaymentMethod={selectedPaymentMethod}
              setSelectedPaymentMethod={handlePaymentMethod}
            />
          </DetailItem>
        )}
        <DetailItem title={`${t('endDate')}:`} bodyStyle={{ alignItems: 'center' }}>
          <S.DatePickerTooltip>
            <DateTimePicker
              dateFormatWithTime="EEE, LLL d - hh:mm aa"
              placeholder={`${t('setEndDate')}*`}
              value={getDatePickerValue()}
              onChange={debounce(date => {
                setExpirationDate(date?.toISOString());
                if (date) {
                  updateRecognition({
                    params: { recognitionCode },
                    body: {
                      settings: {
                        expiresAt: date?.toISOString()
                      }
                    }
                  });
                }
              }, 500)}
              popperPlacement="bottom-start"
              showTimeSelect
              excludePast
              disabled={isApprovalProcessing}
              inputLabel="MM/DD/YYYY"
              {...(isCCPayment && { maxDate: dayjs().add(90, 'days').toDate() })}
            />
          </S.DatePickerTooltip>
        </DetailItem>
        <DetailItem title={`${t('giftCode')}:`}>{recognition?.code}</DetailItem>
        <DetailItem
          editable={isDetailItemEditable}
          bodyStyle={{ alignItems: 'center' }}
          title={`${t('giftName')}:`}
          closeWithoutSave={() => {
            setRecognitionName(recognition.name);
          }}
          saveDetail={() => {
            updateRecognition({
              params: { recognitionCode },
              body: {
                name: recognitionName
              }
            });
          }}
          withBorder
          editInput={() => (
            <Input
              value={recognitionName}
              inputProps={{
                onChange: e => setRecognitionName(e.target.value),
                maxLength: MAX_FEATURE_NAME_LENGTH
              }}
              formItem={{
                label: t('recognitionNameLabel'),
                name: 'recognition_name',
                validations: {
                  required: {
                    required: true
                  }
                }
              }}
            />
          )}
        >
          {recognition?.name}
        </DetailItem>

        {!user?.meta?.isPunchoutUser && (
          <>
            <DetailItem
              editable={isDetailItemEditable}
              title={`${t('po')}#:`}
              bodyStyle={{ alignItems: 'center' }}
              closeWithoutSave={() => {
                setPoNumber(recognition.meta?.poNumber);
              }}
              saveDetail={() => {
                updateRecognition({
                  params: { recognitionCode },
                  body: {
                    meta: {
                      poNumber
                    }
                  }
                });
              }}
              editInput={() => (
                <Input
                  value={poNumber}
                  inputProps={{
                    maxLength: MAX_PO_LENGTH,
                    onChange: e => {
                      setPoNumber(e.target.value);
                    }
                  }}
                  formItem={{
                    label: t('poLabel'),
                    name: 'po'
                  }}
                />
              )}
            >
              {poNumber || t('notSet')}
            </DetailItem>

            <DetailItem
              editable={isDetailItemEditable}
              bodyStyle={{ alignItems: 'center' }}
              closeWithoutSave={() => {
                setCostCenter(recognition.meta?.costCenter);
              }}
              saveDetail={() => {
                updateRecognition({
                  params: { recognitionCode },
                  body: {
                    meta: {
                      costCenter
                    }
                  }
                });
              }}
              editInput={() => (
                <Input
                  value={costCenter}
                  inputProps={{
                    maxLength: MAX_COST_CENTER_LENGTH,
                    onChange: e => {
                      setCostCenter(e.target.value);
                    }
                  }}
                  formItem={{
                    label: t('costCenterLabel'),
                    name: 'costCenter'
                  }}
                />
              )}
              title={`${t('costCenter')}:`}
            >
              {costCenter || t('notSet')}
            </DetailItem>
          </>
        )}

        {!isTeams && advanceSettingsEnabled && (
          <Section>
            <OnboardingCard
              styles={{
                wrapper: {
                  right: -12,
                  top: -12
                }
              }}
              position="right-start"
              cardOffset={[12, 12]}
              isCardShown={isOnboardingCardSettings}
              setIsCardShown={setIsOnboardingCardSettings}
              title={t('settings')}
              description={t('reviewAndPayOverviewOnboardingCard2Desc', {
                feature: t('oneTimeGift')
              })}
              isInfoActive={onboardingRecognition?.infoCardsActive}
              featureType="recognition"
              onTourButtonClick={onClickOnboardingInfoCard}
            />
            <Typography variant="body1Bold">{t('settings')}</Typography>
            {configs.advanceSettings.reGiftingEnabled && (
              <DetailItem title={`${t('itemReGifting')}:`} className="item-d-flex">
                <Typography variant="body2" color="neutral" emphasis="500">
                  {messages.itemReGift}
                </Typography>
                <Toggle
                  checked={!disableReGifting}
                  disabled={!isDetailItemEditable || isLoadingUpdateRecognition}
                  onChange={async () => {
                    setDisableReGifting(!disableReGifting);

                    updateRecognition({
                      params: { recognitionCode },
                      body: {
                        settings: {
                          disableReGifting: !disableReGifting
                        }
                      }
                    });
                  }}
                />
              </DetailItem>
            )}
            {configs.advanceSettings.premiumDates !== false && (
              <DetailItem title={`${t('premiumDates')}:`} className="item-d-flex">
                <Typography variant="body2" color="neutral" emphasis="500">
                  {messages.premiumDates}
                </Typography>
                <Toggle
                  checked={allowPremiumDates}
                  disabled={!isDetailItemEditable || isLoadingUpdateRecognition}
                  onChange={async () => {
                    setAllowPremiumDates(!allowPremiumDates);
                    updateRecognition({
                      params: { recognitionCode },
                      body: {
                        settings: {
                          allowPremiumDates: !allowPremiumDates
                        }
                      }
                    });
                  }}
                />
              </DetailItem>
            )}
            {configs.advanceSettings.enabled && (
              <OverviewAdvanceSettings
                advanceSettings={advanceSettings}
                disabled={!isDetailItemEditable}
                setAdvanceSettings={({
                  settings: { onlyCollectAddress, disableScratchEffect, receiveThankYouNotes }
                }) => {
                  setAdvanceSettings({
                    settings: {
                      onlyCollectAddress,
                      disableScratchEffect,
                      receiveThankYouNotes
                    }
                  });

                  updateRecognition({
                    params: {
                      recognitionCode: recognition.code
                    },
                    body: {
                      settings: {
                        disableScratchEffect: !!disableScratchEffect,
                        receiveThankYouNotes: !!receiveThankYouNotes
                      }
                    }
                  });
                }}
                style={{ marginTop: 24 }}
              />
            )}
          </Section>
        )}
      </CardContent>
      {showCharityInfoModal && (
        <DonateToCharityModal
          isOpen={showCharityInfoModal}
          setIsOpen={setShowCharityInfoModal}
          primaryButtonProps={{
            onClick: () => {
              setShowCharityInfoModal(false);
              setShowSelectCharitiesModal(true);
            },
            children: t('seeAvailableCharities')
          }}
          secondaryButtonProps={{
            onClick: () => setShowCharityInfoModal(false),
            children: t('cancel')
          }}
          descriptionTextKey="donateToCharityDesc3"
        />
      )}
      {showSelectCharitiesModal && (
        <AvailableCharitiesModal
          isOpen={showSelectCharitiesModal}
          setIsOpen={setShowSelectCharitiesModal}
          selectedCharities={[]}
        />
      )}
    </S.OverviewWrapper>
  );
}

const ReviewAndPayOverviewAccordion = withMerchantComponents({
  Component: Overview,
  fileName: 'ReviewAndPayOverviewAccordion',
  atomicFolderName: 'organisms'
});

export { ReviewAndPayOverviewAccordion };
